// pages/PrivacyPolicyPage.jsx
import React, { useState, useCallback } from "react";
import {
  Box,
  Container,
  Typography,
  Link as MLink,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Stack,
  Button,
  Tooltip,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Link as RouterLink } from "react-router-dom";

const LAST_UPDATED = "Feb 21, 2026";
const PAGE_MAX_WIDTH = 1160;
const BODY_TEXT = "#0f172a";
const MUTED_TEXT = "#64748b";

const TOC_ITEMS = [
  { href: "#collect", text: "What we collect & why" },
  { href: "#verification", text: "ID verification (Stripe Identity)" },
  { href: "#use", text: "How we use data" },
  { href: "#sharing", text: "Sharing & processors" },
  { href: "#retention", text: "Retention & your rights" },
  { href: "#security", text: "Security & children" },
  { href: "#changes", text: "Changes & contact" },
];

const processors = [
  { name: "Stripe Identity", purpose: "Government ID verification", data: "ID images/biometrics (processed by Stripe), verification result metadata to us", policyUrl: "https://stripe.com/privacy" },
  { name: "Stripe (Payments)", purpose: "Payment processing", data: "Payment information is processed by Stripe. We may receive limited metadata such as payment status and the last four digits of a card when applicable.", policyUrl: "https://stripe.com/privacy" },
  { name: "Firebase / Google Cloud", purpose: "Auth, database, hosting, logs", data: "Email, UID, IP addresses, usage logs", policyUrl: "https://policies.google.com/privacy" },
  { name: "SendGrid (Twilio)", purpose: "Transactional emails (contact form, support)", data: "Name, email, message content", policyUrl: "https://www.twilio.com/legal/privacy" },
  { name: "OpenAI", purpose: "AI-generated bill analysis summaries", data: "Bill text (public legislation, no personal data)", policyUrl: "https://openai.com/policies/privacy-policy" },
  { name: "OAuth Providers (Google, Facebook, Microsoft)", purpose: "Social sign-in", data: "Name, email, profile identifier from the provider", policyUrl: "https://policies.google.com/privacy" },
];

const SECTION_STYLE = { scrollMarginTop: 96 };

const PRIVACY_MARKDOWN = `# CivixThread — Privacy Policy

Last updated: ${LAST_UPDATED}

CivixThread helps people follow legislation and engage with representatives. This policy explains what we collect, why, how we share it, and your choices. It's written in plain English, no gotchas.

## What we collect & why

We collect only what's needed to power features and maintain authentic, civil discourse.

- **Account & Profile** — Name, email, Firebase UID. Used for login, account integrity, and support.
- **Civic Data You Provide** — Address/ZIP (to determine district), follows, comments, reactions. Used to personalize dashboards and connect you with the right representatives. Posts and comments are visible to other users.
- **Device & Usage** — IP address, device type, operating system, and in-app usage events. Used for security, fraud prevention, and performance.
- **Social Sign-In** — If you sign in with Google, Facebook, or Microsoft, we receive your name, email, and account identifier from the provider. We do not access your contacts, posts, or other data from those services.
- **Optional Analytics / Cookies** — We may use privacy-respecting analytics to understand how features are used. The mobile app does not use cookies. You can opt out of analytics where applicable.
- **Camera and Photo Library** — If you grant permission, the app accesses your camera to scan QR codes at events and capture photos for organization verification. Photo library access is used for profile pictures and post images. Photos you upload are stored securely in Google Cloud Storage.
- **Location** — If you grant permission, the app uses your location to display nearby civic events and determine your congressional district and representatives. Location data is not stored on our servers beyond what is needed to calculate your district.

## ID Verification (Stripe Identity)

To keep conversations real and reduce spam, logging civic actions may require verified identity.

**How it works:** We use Stripe Identity to verify government-issued identification. Stripe processes ID images and biometric data on our behalf.

CivixThread may have limited access to verification information through Stripe's secure dashboard for review and support purposes, but we do not download or store ID images or biometric data in our own systems.

**What we keep:** We keep the verification result and limited metadata for a period necessary to prevent abuse, support appeals, and comply with legal obligations.

## How we use data

- Provide and improve core features (dashboards, bill tracking, comments, alerts).
- Personalize content based on your district and interests.
- Protect community integrity with verified identities and anti-abuse measures.
- Comply with law, enforce terms, and maintain security.
- Analyze anonymized engagement trends to improve civic participation features and reporting.

## Sharing & processors

We do not sell your personal data. We use service providers to operate our product under contracts that restrict their use to our instructions.

CivixThread may use and share anonymized or aggregated information that does not identify you personally—such as general usage trends or public sentiment on legislation—for purposes including improving our services, research, reporting, or supporting civic engagement initiatives.

We also rely on public sources for civic information, such as election and government datasets.

| Processor | Purpose | Types of data | Policy |
|---|---|---|---|
| Stripe Identity | Government ID verification | ID images/biometrics (processed by Stripe), verification result metadata to us | [View](https://stripe.com/privacy) |
| Stripe (Payments) | Payment processing | Payment information is processed by Stripe. We may receive limited metadata such as payment status and the last four digits of a card when applicable. | [View](https://stripe.com/privacy) |
| Firebase / Google Cloud | Auth, database, hosting, logs | Email, UID, IP addresses, usage logs | [View](https://policies.google.com/privacy) |
| SendGrid (Twilio) | Transactional emails (contact form, support) | Name, email, message content | [View](https://www.twilio.com/legal/privacy) |
| OpenAI | AI-generated bill analysis summaries | Bill text (public legislation, no personal data) | [View](https://openai.com/policies/privacy-policy) |
| OAuth Providers (Google, Facebook, Microsoft) | Social sign-in | Name, email, profile identifier from the provider | [View](https://policies.google.com/privacy) |

## Retention & your rights

### Retention
- Account data: kept while your account is active.
- Verification outcomes: kept for a limited period (e.g., 12 to 24 months) to prevent abuse and satisfy compliance.
- Logs/analytics: typically retained for up to 12 months.
- Payments: payment details are handled by Stripe. We do not store full card numbers.

### Your rights
You can access, correct, or delete your data. You can delete your account from the website at civixthread.com/support/delete-account or from Account Settings in the mobile app. Deletion removes your profile, posts, comments, and uploaded images. Email privacy@civixthread.com for other requests. We verify requests and respond within required timeframes. Additional rights may apply in certain jurisdictions.

## Security & children

### Security
We use industry-standard safeguards, including encryption in transit/at rest, access controls, and monitoring. No method is 100% secure, but we work hard to protect your data.

### Children
CivixThread is intended for users who are at least 18 years of age or older. We do not knowingly collect personal data from anyone under 18.

## Changes & contact

We'll update this page for material changes and revise the "Last updated" date. For questions or feedback, reach us anytime.

Contact: privacy@civixthread.com
`;

export default function PrivacyPolicyPage() {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(PRIVACY_MARKDOWN).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, []);

  return (
    <Box sx={{ bgcolor: "#fafafa", minHeight: "100vh", py: { xs: 4, md: 6 } }}>
      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>
        <Box sx={{ maxWidth: PAGE_MAX_WIDTH, mx: "auto" }}>
          {/* Header */}
          <Box sx={{ mb: 5, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: "#0f172a", letterSpacing: "-0.02em", mb: 0.5 }}>
                Privacy Policy
              </Typography>
              <Typography variant="body2" sx={{ color: MUTED_TEXT }}>
                Last updated: {LAST_UPDATED}
              </Typography>
            </Box>
            <Tooltip title={copied ? "Copied!" : "Copy as Markdown"} arrow>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                onClick={handleCopy}
                sx={{
                  textTransform: "none",
                  fontSize: 13,
                  fontWeight: 500,
                  color: copied ? "#047857" : BODY_TEXT,
                  borderColor: copied ? "#047857" : "#94a3b8",
                  bgcolor: copied ? "#f0fdf4" : "#fff",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  minHeight: 36,
                  px: 1.5,
                  py: 0.75,
                  boxShadow: "none",
                  "&:hover": {
                    borderColor: "#047857",
                    color: "#047857",
                    bgcolor: "#f0fdf4",
                    boxShadow: "none",
                  },
                }}
              >
                {copied ? "Copied!" : "Copy as Markdown"}
              </Button>
            </Tooltip>
          </Box>

          {/* Two-column: sidebar left, content right */}
          <Box sx={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
            {/* Sticky sidebar */}
            <Box
              component="nav"
              sx={{
                display: { xs: "none", lg: "block" },
                width: 220,
                flexShrink: 0,
                position: "sticky",
                top: 96,
                borderRight: "1px solid",
                borderColor: "divider",
                pr: 4,
                py: 1,
              }}
            >
              <Typography variant="overline" sx={{ fontWeight: 600, color: MUTED_TEXT, letterSpacing: 1, fontSize: 11, display: "block", mb: 1.5 }}>
                On this page
              </Typography>
              <List dense disablePadding sx={{ "& .MuiListItem-root": { px: 0 } }}>
                {TOC_ITEMS.map((item) => (
                  <ListItem key={item.href} disableGutters sx={{ py: 0.4 }}>
                    <ListItemText
                      primary={
                        <MLink href={item.href} underline="hover" sx={{ fontSize: 14, color: BODY_TEXT, fontWeight: 500, "&:hover": { color: "#047857" } }}>
                          {item.text}
                        </MLink>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>

            {/* Main content */}
            <Box sx={{ flex: 1, maxWidth: 720, minWidth: 0 }}>
              {/* Summary */}
              <Box sx={{ p: 3, mb: 5, bgcolor: "#fff", borderRadius: 2, border: "1px solid", borderColor: "#e2e8f0" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: BODY_TEXT, mb: 1 }}>
                  Our Commitment
                </Typography>
                <Typography sx={{ color: BODY_TEXT, lineHeight: 1.7, fontSize: 15 }}>
                  CivixThread helps people follow legislation and engage with representatives. This policy explains what we collect, why, how we share it, and your choices. It's written in plain English, no gotchas.
                </Typography>
              </Box>

              {/* Mobile ToC */}
              <Box sx={{ display: { lg: "none" }, mb: 4 }}>
                <Typography variant="overline" sx={{ fontWeight: 600, color: MUTED_TEXT, letterSpacing: 1, fontSize: 11 }}>
                  On this page
                </Typography>
                <List dense sx={{ mt: 0.5 }}>
                  {TOC_ITEMS.map((item) => (
                    <ListItem key={item.href} disableGutters sx={{ py: 0.25 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <CheckCircleOutlineIcon fontSize="small" sx={{ color: "#94a3b8" }} />
                      </ListItemIcon>
                      <ListItemText primary={<MLink href={item.href} underline="hover" sx={{ color: "#047857", fontSize: 14 }}>{item.text}</MLink>} />
                    </ListItem>
                  ))}
                </List>
              </Box>

              <Box component="article" sx={{ "& section": { mb: 4 } }}>
                {/* What we collect */}
                <section id="collect" style={SECTION_STYLE}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: BODY_TEXT, mb: 1.5 }}>
                    What we collect & why
                  </Typography>
                  <Typography sx={{ color: BODY_TEXT, lineHeight: 1.7, mb: 2 }}>
                    We collect only what's needed to power features and maintain authentic, civil discourse.
                  </Typography>
                  <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" } }}>
                    {[
                      { title: "Account & Profile", text: "Name, email, Firebase UID. Used for login, account integrity, and support." },
                      { title: "Civic Data You Provide", text: "Address/ZIP (to determine district), follows, comments, reactions. Used to personalize dashboards and connect you with the right representatives. Posts and comments are visible to other users." },
                      { title: "Device & Usage", text: "IP address, device type, operating system, and in-app usage events. Used for security, fraud prevention, and performance." },
                      { title: "Social Sign-In", text: "If you sign in with Google, Facebook, or Microsoft, we receive your name, email, and account identifier from the provider. We do not access your contacts, posts, or other data from those services." },
                      { title: "Optional Analytics / Cookies", text: "We may use privacy-respecting analytics to understand how features are used. The mobile app does not use cookies. You can opt out of analytics where applicable." },
                      { title: "Camera and Photo Library", text: "If you grant permission, the app accesses your camera to scan QR codes at events and capture photos for organization verification. Photo library access is used for profile pictures and post images. Photos you upload are stored securely in Google Cloud Storage." },
                      { title: "Location", text: "If you grant permission, the app uses your location to display nearby civic events and determine your congressional district and representatives. Location data is not stored on our servers beyond what is needed to calculate your district." },
                    ].map(({ title, text }) => (
                      <Box key={title} sx={{ p: 2, bgcolor: "#fff", borderRadius: 2, border: "1px solid", borderColor: "#e2e8f0" }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: BODY_TEXT, mb: 0.5 }}>{title}</Typography>
                        <Typography variant="body2" sx={{ color: BODY_TEXT, lineHeight: 1.6 }}>{text}</Typography>
                      </Box>
                    ))}
                  </Box>
                </section>

                {/* ID Verification */}
                <section id="verification" style={SECTION_STYLE}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: BODY_TEXT, mb: 1.5 }}>
                    ID Verification (Stripe Identity)
                  </Typography>
                  <Typography sx={{ color: BODY_TEXT, lineHeight: 1.7, mb: 1 }}>
                    To keep conversations real and reduce spam, logging civic actions may require verified identity.
                  </Typography>
                  <Accordion disableGutters sx={{ borderRadius: 1, overflow: "hidden", border: "1px solid #e2e8f0", "&:before": { display: "none" }, mb: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: "#f8fafc" }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: BODY_TEXT }}>How it works</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      <Typography variant="body2" sx={{ color: BODY_TEXT, lineHeight: 1.6 }}>
                        We use <MLink href="https://stripe.com/identity" target="_blank" rel="noopener noreferrer" sx={{ color: "#047857", fontWeight: 500 }}>Stripe Identity</MLink> to verify government-issued identification. Stripe processes ID images and biometric data on our behalf.
                      </Typography>
                      <Typography variant="body2" sx={{ color: BODY_TEXT, lineHeight: 1.6, mt: 1 }}>
                        CivixThread may have limited access to verification information through Stripe's secure dashboard for review and support purposes, but we do not download or store ID images or biometric data in our own systems.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                  <Accordion disableGutters sx={{ borderRadius: 1, overflow: "hidden", border: "1px solid #e2e8f0", "&:before": { display: "none" } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: "#f8fafc" }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: BODY_TEXT }}>What we keep</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      <Typography variant="body2" sx={{ color: BODY_TEXT, lineHeight: 1.6 }}>
                        We keep the verification result and limited metadata for a period necessary to prevent abuse, support appeals, and comply with legal obligations.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </section>

                {/* How we use data */}
                <section id="use" style={SECTION_STYLE}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: BODY_TEXT, mb: 1.5 }}>
                    How we use data
                  </Typography>
                  <List dense sx={{ pl: 1 }}>
                    {[
                      "Provide and improve core features (dashboards, bill tracking, comments, alerts).",
                      "Personalize content based on your district and interests.",
                      "Protect community integrity with verified identities and anti-abuse measures.",
                      "Comply with law, enforce terms, and maintain security.",
                      "Analyze anonymized engagement trends to improve civic participation features and reporting.",
                    ].map((text) => (
                      <ListItem key={text} sx={{ py: 0.2, display: "list-item", listStyleType: "disc" }}>
                        <ListItemText primary={text} primaryTypographyProps={{ variant: "body2", sx: { color: BODY_TEXT, lineHeight: 1.6 } }} />
                      </ListItem>
                    ))}
                  </List>
                </section>

                {/* Sharing & processors */}
                <section id="sharing" style={SECTION_STYLE}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: BODY_TEXT, mb: 1.5 }}>
                    Sharing & processors
                  </Typography>
                  <Typography sx={{ color: BODY_TEXT, lineHeight: 1.7, mb: 1 }}>
                    We do not sell your personal data. We use service providers to operate our product under contracts that restrict their use to our instructions.
                  </Typography>
                  <Typography sx={{ color: BODY_TEXT, lineHeight: 1.7, mb: 1 }}>
                    CivixThread may use and share anonymized or aggregated information that does not identify you personally—such as general usage trends or public sentiment on legislation—for purposes including improving our services, research, reporting, or supporting civic engagement initiatives.
                  </Typography>
                  <Typography sx={{ color: BODY_TEXT, lineHeight: 1.7, mb: 2 }}>
                    We also rely on public sources for civic information, such as election and government datasets.
                  </Typography>
                  <Box sx={{ border: "1px solid #e2e8f0", borderRadius: 2, overflow: "hidden", bgcolor: "#fff" }}>
                    <TableContainer>
                      <Table size="small" aria-label="Processors table">
                        <TableHead>
                          <TableRow sx={{ bgcolor: "#f8fafc" }}>
                            <TableCell sx={{ fontWeight: 600, color: "#0f172a" }}>Processor</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: "#0f172a" }}>Purpose</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: "#0f172a" }}>Types of data</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: "#0f172a" }}>Policy</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {processors.map((p) => (
                            <TableRow key={p.name}>
                              <TableCell sx={{ color: BODY_TEXT, fontSize: 13 }}>{p.name}</TableCell>
                              <TableCell sx={{ color: BODY_TEXT, fontSize: 13 }}>{p.purpose}</TableCell>
                              <TableCell sx={{ color: BODY_TEXT, fontSize: 13 }}>{p.data}</TableCell>
                              <TableCell>
                                <MLink href={p.policyUrl} target="_blank" rel="noopener noreferrer" sx={{ color: "#047857", fontWeight: 500, fontSize: 13 }}>View</MLink>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </section>

                {/* Retention & rights */}
                <section id="retention" style={SECTION_STYLE}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: BODY_TEXT, mb: 1.5 }}>
                    Retention & your rights
                  </Typography>
                  <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" } }}>
                    <Box sx={{ p: 2, bgcolor: "#fff", borderRadius: 2, border: "1px solid", borderColor: "#e2e8f0" }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: BODY_TEXT, mb: 1 }}>Retention</Typography>
                      <Typography variant="body2" sx={{ color: BODY_TEXT, lineHeight: 1.6 }}>
                        • Account data: kept while your account is active.<br />
                        • Verification outcomes: kept for a limited period (e.g., 12 to 24 months) to prevent abuse and satisfy compliance.<br />
                        • Logs/analytics: typically retained for up to 12 months.<br />
                        • Payments: payment details are handled by Stripe. We do not store full card numbers.
                      </Typography>
                    </Box>
                    <Box sx={{ p: 2, bgcolor: "#fff", borderRadius: 2, border: "1px solid", borderColor: "#e2e8f0" }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: BODY_TEXT, mb: 1 }}>Your rights</Typography>
                      <Typography variant="body2" sx={{ color: BODY_TEXT, lineHeight: 1.6, mb: 1 }}>
                        You can access, correct, or delete your data. You can delete your account from the{" "}
                        <MLink component={RouterLink} to="/support/delete-account" sx={{ color: "#047857", fontWeight: 500 }}>
                          account deletion page
                        </MLink>{" "}
                        on the website or from Account Settings in the mobile app. Deletion removes your profile, posts, comments, and uploaded images. Email{" "}
                        <MLink href="mailto:privacy@civixthread.com" sx={{ color: "#047857", fontWeight: 500 }}>privacy@civixthread.com</MLink> for other requests. We verify requests and respond within required timeframes. Additional rights may apply in certain jurisdictions.
                      </Typography>
                    </Box>
                  </Box>
                </section>

                {/* Security & children */}
                <section id="security" style={SECTION_STYLE}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: BODY_TEXT, mb: 1.5 }}>
                    Security & children
                  </Typography>
                  <Box sx={{ p: 2, bgcolor: "#fff", borderRadius: 2, border: "1px solid", borderColor: "#e2e8f0" }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: BODY_TEXT, mb: 1 }}>Security</Typography>
                    <Typography variant="body2" sx={{ color: BODY_TEXT, lineHeight: 1.6, mb: 2 }}>
                      We use industry-standard safeguards, including encryption in transit/at rest, access controls, and monitoring. No method is 100% secure, but we work hard to protect your data.
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: BODY_TEXT, mb: 1 }}>Children</Typography>
                    <Typography variant="body2" sx={{ color: BODY_TEXT, lineHeight: 1.6 }}>
                      CivixThread is intended for users who are at least 18 years of age or older. We do not knowingly collect personal data from anyone under 18.
                    </Typography>
                  </Box>
                </section>

                {/* Changes & contact */}
                <section id="changes" style={SECTION_STYLE}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: BODY_TEXT, mb: 1.5 }}>
                    Changes & contact
                  </Typography>
                  <Typography sx={{ color: BODY_TEXT, lineHeight: 1.7, mb: 2 }}>
                    We'll update this page for material changes and revise the "Last updated" date. For questions or feedback, reach us anytime.
                  </Typography>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                    <Button variant="contained" size="small" startIcon={<ArrowBackIosNewIcon sx={{ fontSize: 16 }} />} href="/"
                      sx={{ bgcolor: "#047857", color: "#ffffff", "&:hover": { bgcolor: "#065f46", color: "#ffffff" } }}>
                      Back to home
                    </Button>
                  </Stack>
                </section>
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
